import { useState, useEffect } from 'react';
import { Users, Plus, Search, Edit2, Trash2, Save, X, ChevronDown, Shield } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { fetchAllRows } from '../../lib/fetchAllRows';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';

type DriverCategory = 'all_drivers' | 'hired' | 'ready_waiting' | 're_possible' | 'not_hired';

const CATEGORY_LABELS: Record<DriverCategory, string> = {
  all_drivers: 'ALL DRIVERS',
  ready_waiting: 'READY - WAITING FOR FREE TRUCK',
  hired: 'HIRED DRIVERS',
  re_possible: 'RE-POSSIBLE FOR HIRE',
  not_hired: 'NOT HIRED - NOT RE-POSSIBLE FOR HIRE',
};

// Canonical display order for category sections + dropdowns + stats footer.
const CATEGORY_ORDER: DriverCategory[] = [
  'all_drivers',
  'ready_waiting',
  'hired',
  're_possible',
  'not_hired',
];

interface RecruitingDriver {
  id: string;
  name: string;
  first_name?: string | null;
  last_name?: string | null;
  person: string | null;
  date_of_arrival: string | null;
  status: string | null;
  position: string | null;
  company: string | null;
  phone_number: string | null;
  phone?: string | null;
  email: string | null;
  commission_cpm: string | null;
  reference: string | null;
  application: string | null;
  mvr: string | null;
  psp: string | null;
  medical: string | null;
  cdl_mvr_match: string | null;
  medical_mvr_match: string | null;
  clh: string | null;
  two_year_proof: string | null;
  cdl_scan: string | null;
  drug_test: string | null;
  last_updated: string | null;
  category: DriverCategory;
  recruiter_email?: string | null;
  source?: string | null;
  notes?: string | null;
  // mirror fields used by Send-to-Safety
  date_of_birth?: string | null;
  ssn?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip_code?: string | null;
  company_id?: string | null;
  cdl_class?: string | null;
  cdl_number?: string | null;
  cdl_state?: string | null;
  cdl_expiration?: string | null;
  mvr_status?: string | null;
  mvr_expiration_date?: string | null;
  mvr_notes?: string | null;
  mvr_document_url?: string | null;
  medical_card_expiration?: string | null;
  medical_document_url?: string | null;
  clearinghouse_expiration_date?: string | null;
  hire_date?: string | null;
  application_date?: string | null;
  application_form_url?: string | null;
  accident_history?: string | null;
  violation_history?: string | null;
  road_test_date?: string | null;
  road_test_passed?: boolean | null;
  road_test_certificate_url?: string | null;
  previous_employer_1?: string | null;
  previous_employer_2?: string | null;
  previous_employer_3?: string | null;
  employment_verification_date?: string | null;
  employment_verification_url?: string | null;
  hazmat_endorsement?: boolean | null;
  hazmat_expiration?: string | null;
  tanker_endorsement?: boolean | null;
  doubles_triples_endorsement?: boolean | null;
  passenger_endorsement?: boolean | null;
  other_certifications?: string | null;
  other_certifications_url?: string | null;
  cdl_photo_url?: string | null;
  twic_photo_url?: string | null;
  pilot_fuel_card?: string | null;
  loves_fuel_card?: string | null;
  created_at: string;
  updated_at: string;
}

interface PipelineLead {
  id: string;
  name: string | null;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  email: string | null;
  status: string;
  recruiter: string | null;
  source: string | null;
  notes: string | null;
  interested_in: string | null;
  updated_at: string;
}

export function RecruitingAllDriversView() {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [drivers, setDrivers] = useState<RecruitingDriver[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPerson, setFilterPerson] = useState('');
  const [filterCompany, setFilterCompany] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<RecruitingDriver>>({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [allDriversExpanded, setAllDriversExpanded] = useState(true);
  const [hiredExpanded, setHiredExpanded] = useState(true);
  const [readyWaitingExpanded, setReadyWaitingExpanded] = useState(true);
  const [rePossibleExpanded, setRePossibleExpanded] = useState(true);
  const [notHiredExpanded, setNotHiredExpanded] = useState(true);
  const [pipelineExpanded, setPipelineExpanded] = useState(true);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [recruiters, setRecruiters] = useState<string[]>([]);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedDriverForStatus, setSelectedDriverForStatus] = useState<RecruitingDriver | null>(null);
  const [newStatus, setNewStatus] = useState('');
  const [pipelineLeads, setPipelineLeads] = useState<PipelineLead[]>([]);
  const [promotingLeadId, setPromotingLeadId] = useState<string | null>(null);
  const [sendingToSafety, setSendingToSafety] = useState(false);
  const [newDriver, setNewDriver] = useState<Partial<RecruitingDriver>>({
    name: '',
    person: '',
    date_of_arrival: '',
    status: '',
    position: '',
    company: '',
    phone_number: '',
    email: '',
    commission_cpm: '',
    reference: '',
    application: '',
    mvr: '',
    psp: '',
    medical: '',
    cdl_mvr_match: '',
    medical_mvr_match: '',
    clh: '',
    two_year_proof: '',
    cdl_scan: '',
    drug_test: '',
    category: 'all_drivers'
  });

  const statusOptions = ['Working on it', 'Done', 'Pending'];
  const positionOptions = ['COMPANY', 'OWNER', 'LEASE TO PURCHASE'];
  const companyOptions = ['APOLLO', 'TESLA', 'TMARK'];
  const yesNoOptions = ['YES', 'NO', 'N/A'];
  const documentStatusOptions = ['Working on it', 'Done', 'DONE', 'WAITING FOR IT', 'Pending', 'N/A'];

  useEffect(() => {
    // Order matters: dedup first so the post-sync fetch sees a clean table,
    // then sync any newly-hired CRM leads into the pipeline, then load.
    (async () => {
      await dedupDriversByContact();
      await syncHiredCrmLeadsIntoPipeline();
      await Promise.all([fetchDrivers(), fetchPipelineLeads(), fetchRecruiters()]);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchRecruiters = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('full_name')
        .eq('role', 'Recruiting');

      if (error) throw error;

      const recruiterNames = data
        ?.map((profile: any) => profile.full_name)
        .filter((name: string | null) => name != null) || [];

      setRecruiters(recruiterNames);
    } catch (error) {
      console.error('Error fetching recruiters:', error);
      setRecruiters([]);
    }
  };

  const fetchDrivers = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('recruiting_all_drivers')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setDrivers((data as RecruitingDriver[]) || []);
    } catch (error) {
      console.error('Error fetching drivers:', error);
      showToast('error', 'Failed to load drivers');
    } finally {
      setLoading(false);
    }
  };

  const fetchPipelineLeads = async () => {
    try {
      const data = await fetchAllRows<any>(() => supabase
        .from('crm_leads')
        .select('id, name, first_name, last_name, phone, email, status, recruiter, source, notes, interested_in, updated_at')
        .in('status', ['approved', 'drug_test_scheduled', 'drug_test_pass'])
        .order('updated_at', { ascending: false }));
      setPipelineLeads((data as PipelineLead[]) || []);
    } catch (error) {
      console.error('Error fetching pipeline leads:', error);
      setPipelineLeads([]);
    }
  };

  // On mount: collapse duplicate recruiting rows by email, then phone.
  // Newest updated_at wins; older copies are deleted.
  const dedupDriversByContact = async () => {
    try {
      const { data, error } = await supabase
        .from('recruiting_all_drivers')
        .select('id, email, phone, phone_number, updated_at')
        .order('updated_at', { ascending: false });
      if (error || !data) return;

      const seenEmail = new Set<string>();
      const seenPhone = new Set<string>();
      const toDelete: string[] = [];
      for (const row of data as Array<{ id: string; email: string | null; phone: string | null; phone_number: string | null }>) {
        const emailKey = (row.email || '').trim().toLowerCase();
        const phoneKey = (row.phone_number || row.phone || '').replace(/\D/g, '');
        let isDup = false;
        if (emailKey) {
          if (seenEmail.has(emailKey)) isDup = true;
          else seenEmail.add(emailKey);
        }
        if (!isDup && phoneKey) {
          if (seenPhone.has(phoneKey)) isDup = true;
          else seenPhone.add(phoneKey);
        }
        if (isDup) toDelete.push(row.id);
      }
      if (toDelete.length > 0) {
        await supabase.from('recruiting_all_drivers').delete().in('id', toDelete);
      }
    } catch (error) {
      console.error('Dedup failed:', error);
    }
  };

  // On mount: pull any crm_leads.status='hired' not yet in the pipeline.
  // Match by email first, then phone — same dedup key as above.
  const syncHiredCrmLeadsIntoPipeline = async () => {
    try {
      const hiredLeads = await fetchAllRows<any>(() => supabase
        .from('crm_leads')
        .select('id, name, first_name, last_name, phone, email, source, notes, recruiter, updated_at')
        .eq('status', 'hired'));
      if (hiredLeads.length === 0) return;

      const { data: existing } = await supabase
        .from('recruiting_all_drivers')
        .select('email, phone, phone_number');
      const existingEmails = new Set(
        (existing || []).map((r: any) => (r.email || '').trim().toLowerCase()).filter(Boolean)
      );
      const existingPhones = new Set(
        (existing || []).map((r: any) => (r.phone_number || r.phone || '').replace(/\D/g, '')).filter(Boolean)
      );

      const toInsert = (hiredLeads as any[])
        .filter(l => {
          const emailKey = (l.email || '').trim().toLowerCase();
          const phoneKey = (l.phone || '').replace(/\D/g, '');
          if (emailKey && existingEmails.has(emailKey)) return false;
          if (phoneKey && existingPhones.has(phoneKey)) return false;
          return true;
        })
        .map(l => ({
          name: l.name || `${l.first_name || ''} ${l.last_name || ''}`.trim() || null,
          first_name: l.first_name,
          last_name: l.last_name,
          email: l.email,
          phone: l.phone,
          phone_number: l.phone,
          person: l.recruiter,
          source: l.source,
          notes: l.notes,
          status: 'Working on it',
          category: 'hired',
        }));

      if (toInsert.length > 0) {
        await supabase.from('recruiting_all_drivers').insert(toInsert);
      }
    } catch (error) {
      console.error('Sync hired leads failed:', error);
    }
  };

  const moveLeadToPipeline = async (lead: PipelineLead, category: DriverCategory) => {
    setPromotingLeadId(lead.id);
    try {
      const insertPayload = {
        name: lead.name || `${lead.first_name || ''} ${lead.last_name || ''}`.trim() || null,
        first_name: lead.first_name,
        last_name: lead.last_name,
        email: lead.email,
        phone: lead.phone,
        phone_number: lead.phone,
        person: lead.recruiter,
        source: lead.source,
        notes: lead.notes,
        status: 'Working on it',
        category,
      };
      const { error } = await supabase.from('recruiting_all_drivers').insert([insertPayload]);
      if (error) throw error;
      // If they moved into Hired, also flip the source lead to status='hired'
      // so the auto-sync logic doesn't try to re-add it on next mount.
      if (category === 'hired') {
        await supabase.from('crm_leads').update({ status: 'hired' }).eq('id', lead.id);
      }
      showToast('success', `Moved ${insertPayload.name || 'lead'} → ${CATEGORY_LABELS[category]}`);
      await Promise.all([fetchDrivers(), fetchPipelineLeads()]);
    } catch (error) {
      console.error('Promote lead failed:', error);
      showToast('error', 'Failed to move lead');
    } finally {
      setPromotingLeadId(null);
    }
  };

  const handleCategoryChange = async (driverId: string, newCategory: DriverCategory) => {
    try {
      const { error } = await supabase
        .from('recruiting_all_drivers')
        .update({ category: newCategory })
        .eq('id', driverId);
      if (error) throw error;
      showToast('success', `Moved to ${CATEGORY_LABELS[newCategory]}`);
      await fetchDrivers();
    } catch (error) {
      console.error('Category change failed:', error);
      showToast('error', 'Failed to move driver');
    }
  };

  // Send to Safety: promote one Hired recruiting record into the real drivers
  // table, then mark the source as fully hired. Single-row only by spec.
  const handleSendToSafety = async () => {
    if (selectedIds.length !== 1) return;
    const source = drivers.find(d => d.id === selectedIds[0]);
    if (!source) return;
    if (source.category !== 'hired') {
      showToast('error', 'Driver must be in HIRED category');
      return;
    }
    if (!confirm(`Promote ${source.name || 'this driver'} to Safety? This creates a real driver record.`)) return;

    setSendingToSafety(true);
    try {
      const [firstName, ...rest] = (source.name || '').trim().split(/\s+/);
      const driverPayload: Record<string, unknown> = {
        first_name: source.first_name || firstName || source.name || 'Unknown',
        last_name: source.last_name || rest.join(' ') || null,
        email: source.email,
        phone: source.phone_number || source.phone,
        company_id: source.company_id,
        date_of_birth: source.date_of_birth,
        ssn: source.ssn,
        address: source.address,
        city: source.city,
        state: source.state,
        zip_code: source.zip_code,
        cdl_number: source.cdl_number,
        cdl_state: source.cdl_state,
        cdl_expiration: source.cdl_expiration,
        cdl_class: source.cdl_class,
        mvr_status: source.mvr_status,
        mvr_expiration_date: source.mvr_expiration_date,
        mvr_notes: source.mvr_notes,
        mvr_document_url: source.mvr_document_url,
        medical_card_expiration: source.medical_card_expiration,
        medical_document_url: source.medical_document_url,
        clearinghouse_expiration_date: source.clearinghouse_expiration_date,
        hire_date: source.hire_date || new Date().toISOString().slice(0, 10),
        driver_status: 'active',
        driver_type: source.position === 'COMPANY' ? 'company' : source.position === 'OWNER' ? 'owner_operator' : source.position === 'LEASE TO PURCHASE' ? 'lease' : null,
        application_date: source.application_date,
        application_form_url: source.application_form_url,
        accident_history: source.accident_history,
        violation_history: source.violation_history,
        road_test_date: source.road_test_date,
        road_test_passed: source.road_test_passed,
        road_test_certificate_url: source.road_test_certificate_url,
        previous_employer_1: source.previous_employer_1,
        previous_employer_2: source.previous_employer_2,
        previous_employer_3: source.previous_employer_3,
        employment_verification_date: source.employment_verification_date,
        employment_verification_url: source.employment_verification_url,
        hazmat_endorsement: source.hazmat_endorsement,
        hazmat_expiration: source.hazmat_expiration,
        tanker_endorsement: source.tanker_endorsement,
        doubles_triples_endorsement: source.doubles_triples_endorsement,
        passenger_endorsement: source.passenger_endorsement,
        other_certifications: source.other_certifications,
        other_certifications_url: source.other_certifications_url,
        cdl_photo_url: source.cdl_photo_url,
        twic_photo_url: source.twic_photo_url,
        pilot_fuel_card: source.pilot_fuel_card,
        loves_fuel_card: source.loves_fuel_card,
        recruiter_email: source.recruiter_email,
        is_active: true,
      };
      // Strip nullish entries so we don't overwrite drivers defaults with null.
      Object.keys(driverPayload).forEach(k => {
        if (driverPayload[k] === undefined || driverPayload[k] === null || driverPayload[k] === '') {
          delete driverPayload[k];
        }
      });

      const { error: insertErr } = await supabase.from('drivers').insert([driverPayload]);
      if (insertErr) throw insertErr;

      // Mark the recruiting row as fully promoted (status=Done) so it doesn't get re-promoted.
      await supabase
        .from('recruiting_all_drivers')
        .update({ status: 'Done', category: 'hired' })
        .eq('id', source.id);

      showToast('success', `${source.name || 'Driver'} sent to Safety`);
      setSelectedIds([]);
      await fetchDrivers();
    } catch (error: any) {
      console.error('Send to Safety failed:', error);
      showToast('error', error?.message || 'Failed to send to Safety');
    } finally {
      setSendingToSafety(false);
    }
  };

  const handleAdd = async () => {
    if (!newDriver.name || !newDriver.person) {
      showToast('error', 'Please fill in required fields (Name, Person)');
      return;
    }

    try {
      const { data: userData } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', user?.id)
        .single();

      const { error } = await supabase
        .from('recruiting_all_drivers')
        .insert([{
          ...newDriver,
          company_id: userData?.company_id,
          last_updated: new Date().toISOString()
        }]);

      if (error) throw error;

      showToast('success', 'Driver added successfully');
      setShowAddForm(false);
      setNewDriver({
        name: '',
        person: '',
        date_of_arrival: '',
        status: '',
        position: '',
        company: '',
        phone_number: '',
        email: '',
        commission_cpm: '',
        reference: '',
        application: '',
        mvr: '',
        psp: '',
        medical: '',
        cdl_mvr_match: '',
        medical_mvr_match: '',
        clh: '',
        two_year_proof: '',
        cdl_scan: '',
        drug_test: '',
        category: 'all_drivers'
      });
      fetchDrivers();
    } catch (error) {
      console.error('Error adding driver:', error);
      showToast('error', 'Failed to add driver');
    }
  };

  const handleEdit = (driver: RecruitingDriver) => {
    setEditingId(driver.id);
    setEditForm(driver);
  };

  const handleSave = async () => {
    if (!editingId) return;

    try {
      const { error } = await supabase
        .from('recruiting_all_drivers')
        .update({
          ...editForm,
          last_updated: new Date().toISOString()
        })
        .eq('id', editingId);

      if (error) throw error;

      showToast('success', 'Driver updated successfully');
      setEditingId(null);
      setEditForm({});
      fetchDrivers();
    } catch (error) {
      console.error('Error updating driver:', error);
      showToast('error', 'Failed to update driver');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this driver?')) return;

    try {
      const { error } = await supabase
        .from('recruiting_all_drivers')
        .delete()
        .eq('id', id);

      if (error) throw error;

      showToast('success', 'Driver deleted successfully');
      fetchDrivers();
    } catch (error) {
      console.error('Error deleting driver:', error);
      showToast('error', 'Failed to delete driver');
    }
  };

  const handleRowClick = (id: string) => {
    setSelectedIds(prev => 
      prev.includes(id) 
        ? prev.filter(rowId => rowId !== id)
        : [...prev, id]
    );
  };

  const handleStatusClick = (driver: RecruitingDriver, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedDriverForStatus(driver);
    setNewStatus(driver.status || '');
    setShowStatusModal(true);
  };

  const handleStatusUpdate = async () => {
    if (!selectedDriverForStatus) return;

    try {
      const { error } = await supabase
        .from('recruiting_all_drivers')
        .update({
          status: newStatus,
          last_updated: new Date().toISOString()
        })
        .eq('id', selectedDriverForStatus.id);

      if (error) throw error;

      showToast('success', 'Status updated successfully');
      setShowStatusModal(false);
      setSelectedDriverForStatus(null);
      fetchDrivers();
    } catch (error) {
      console.error('Error updating status:', error);
      showToast('error', 'Failed to update status');
    }
  };

  const filteredDrivers = drivers.filter(driver => {
    const matchesSearch = driver.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         driver.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         driver.phone_number?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesPerson = !filterPerson || driver.person === filterPerson;
    const matchesCompany = !filterCompany || driver.company === filterCompany;
    return matchesSearch && matchesPerson && matchesCompany;
  });

  const allDriversEntries = filteredDrivers.filter(d => d.category === 'all_drivers');
  const hiredEntries = filteredDrivers.filter(d => d.category === 'hired');
  const readyWaitingEntries = filteredDrivers.filter(d => d.category === 'ready_waiting');
  const rePossibleEntries = filteredDrivers.filter(d => d.category === 're_possible');
  const notHiredEntries = filteredDrivers.filter(d => d.category === 'not_hired');

  const getStatusColor = (status: string | null) => {
    switch (status) {
      case 'Done': return 'bg-green-100 text-green-800';
      case 'Working on it': return 'bg-yellow-100 text-yellow-800';
      case 'Pending': return 'bg-orange-100 text-orange-800';
      default: return 'bg-slate-100 text-slate-800';
    }
  };

  const getPositionColor = (position: string | null) => {
    switch (position) {
      case 'COMPANY': return 'bg-blue-100 text-blue-800';
      case 'OWNER': return 'bg-purple-100 text-purple-800';
      case 'LEASE TO PURCHASE': return 'bg-green-100 text-green-800';
      default: return 'bg-slate-100 text-slate-800';
    }
  };

  const renderTableRows = (entries: RecruitingDriver[]) => {
    if (entries.length === 0) {
      return (
        <tr>
          <td colSpan={23} className="px-4 py-12 text-center text-slate-500">
            No drivers found.
          </td>
        </tr>
      );
    }

    return entries.map(driver => (
      <tr 
        key={driver.id} 
        onClick={() => editingId !== driver.id && handleRowClick(driver.id)}
        className={`hover:bg-slate-50 cursor-pointer transition-colors ${selectedIds.includes(driver.id) ? 'bg-blue-50' : ''}`}
      >
        {editingId === driver.id ? (
          <>
            <td className="px-2 py-2">
              <input
                type="checkbox"
                checked={selectedIds.includes(driver.id)}
                onChange={(e) => {
                  e.stopPropagation();
                  handleRowClick(driver.id);
                }}
                className="w-4 h-4 text-blue-600 rounded"
              />
            </td>
            <td className="px-2 py-2">
              <input type="text" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="w-full px-2 py-1 border rounded text-xs" />
            </td>
            <td className="px-2 py-2">
              <select value={editForm.person || ''} onChange={(e) => setEditForm({ ...editForm, person: e.target.value })} className="w-full px-2 py-1 border rounded text-xs">
                <option value="">Select</option>
                {recruiters.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </td>
            <td className="px-2 py-2">
              <input type="date" value={editForm.date_of_arrival || ''} onChange={(e) => setEditForm({ ...editForm, date_of_arrival: e.target.value })} className="w-full px-2 py-1 border rounded text-xs" />
            </td>
            <td className="px-2 py-2">
              <select value={editForm.status || ''} onChange={(e) => setEditForm({ ...editForm, status: e.target.value })} className="w-full px-2 py-1 border rounded text-xs">
                <option value="">Select</option>
                {statusOptions.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </td>
            <td className="px-2 py-2">
              <select value={editForm.position || ''} onChange={(e) => setEditForm({ ...editForm, position: e.target.value })} className="w-full px-2 py-1 border rounded text-xs">
                <option value="">Select</option>
                {positionOptions.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </td>
            <td className="px-2 py-2">
              <select value={editForm.company || ''} onChange={(e) => setEditForm({ ...editForm, company: e.target.value })} className="w-full px-2 py-1 border rounded text-xs">
                <option value="">Select</option>
                {companyOptions.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </td>
            <td className="px-2 py-2">
              <input type="text" value={editForm.phone_number || ''} onChange={(e) => setEditForm({ ...editForm, phone_number: e.target.value })} className="w-full px-2 py-1 border rounded text-xs" />
            </td>
            <td className="px-2 py-2">
              <input type="email" value={editForm.email || ''} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} className="w-full px-2 py-1 border rounded text-xs" />
            </td>
            <td className="px-2 py-2">
              <input type="text" value={editForm.commission_cpm || ''} onChange={(e) => setEditForm({ ...editForm, commission_cpm: e.target.value })} className="w-full px-2 py-1 border rounded text-xs" />
            </td>
            <td className="px-2 py-2">
              <select value={editForm.reference || ''} onChange={(e) => setEditForm({ ...editForm, reference: e.target.value })} className="w-full px-2 py-1 border rounded text-xs">
                <option value="">Select</option>
                {documentStatusOptions.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </td>
            <td className="px-2 py-2">
              <select value={editForm.application || ''} onChange={(e) => setEditForm({ ...editForm, application: e.target.value })} className="w-full px-2 py-1 border rounded text-xs">
                <option value="">Select</option>
                {documentStatusOptions.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </td>
            <td className="px-2 py-2">
              <select value={editForm.mvr || ''} onChange={(e) => setEditForm({ ...editForm, mvr: e.target.value })} className="w-full px-2 py-1 border rounded text-xs">
                <option value="">Select</option>
                {documentStatusOptions.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </td>
            <td className="px-2 py-2">
              <select value={editForm.psp || ''} onChange={(e) => setEditForm({ ...editForm, psp: e.target.value })} className="w-full px-2 py-1 border rounded text-xs">
                <option value="">Select</option>
                {documentStatusOptions.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </td>
            <td className="px-2 py-2">
              <select value={editForm.medical || ''} onChange={(e) => setEditForm({ ...editForm, medical: e.target.value })} className="w-full px-2 py-1 border rounded text-xs">
                <option value="">Select</option>
                {documentStatusOptions.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </td>
            <td className="px-2 py-2">
              <select value={editForm.cdl_mvr_match || ''} onChange={(e) => setEditForm({ ...editForm, cdl_mvr_match: e.target.value })} className="w-full px-2 py-1 border rounded text-xs">
                <option value="">Select</option>
                {yesNoOptions.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </td>
            <td className="px-2 py-2">
              <select value={editForm.medical_mvr_match || ''} onChange={(e) => setEditForm({ ...editForm, medical_mvr_match: e.target.value })} className="w-full px-2 py-1 border rounded text-xs">
                <option value="">Select</option>
                {yesNoOptions.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </td>
            <td className="px-2 py-2">
              <select value={editForm.clh || ''} onChange={(e) => setEditForm({ ...editForm, clh: e.target.value })} className="w-full px-2 py-1 border rounded text-xs">
                <option value="">Select</option>
                {documentStatusOptions.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </td>
            <td className="px-2 py-2">
              <select value={editForm.two_year_proof || ''} onChange={(e) => setEditForm({ ...editForm, two_year_proof: e.target.value })} className="w-full px-2 py-1 border rounded text-xs">
                <option value="">Select</option>
                {yesNoOptions.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </td>
            <td className="px-2 py-2">
              <select value={editForm.cdl_scan || ''} onChange={(e) => setEditForm({ ...editForm, cdl_scan: e.target.value })} className="w-full px-2 py-1 border rounded text-xs">
                <option value="">Select</option>
                {yesNoOptions.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </td>
            <td className="px-2 py-2">
              <select value={editForm.drug_test || ''} onChange={(e) => setEditForm({ ...editForm, drug_test: e.target.value })} className="w-full px-2 py-1 border rounded text-xs">
                <option value="">Select</option>
                {documentStatusOptions.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </td>
            <td className="px-2 py-2">
              <select value={editForm.category || 'all_drivers'} onChange={(e) => setEditForm({ ...editForm, category: e.target.value as DriverCategory })} className="w-full px-2 py-1 border rounded text-xs">
                {CATEGORY_ORDER.map(c => (
                  <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>
                ))}
              </select>
            </td>
            <td className="px-2 py-2">
              <div className="flex items-center gap-1">
                <button onClick={handleSave} className="p-1 text-green-600 hover:bg-green-50 rounded">
                  <Save className="w-4 h-4" />
                </button>
                <button onClick={() => { setEditingId(null); setEditForm({}); }} className="p-1 text-slate-600 hover:bg-slate-50 rounded">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </td>
          </>
        ) : (
          <>
            <td className="px-2 py-2" onClick={(e) => e.stopPropagation()}>
              <input type="checkbox" checked={selectedIds.includes(driver.id)} onChange={(e) => { e.stopPropagation(); handleRowClick(driver.id); }} className="w-4 h-4 text-blue-600 rounded" />
            </td>
            <td className="px-2 py-2 text-xs font-medium text-slate-900">{driver.name}</td>
            <td className="px-2 py-2"><span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">{driver.person || '-'}</span></td>
            <td className="px-2 py-2 text-xs text-slate-600">{driver.date_of_arrival || '-'}</td>
            <td className="px-2 py-2" onClick={(e) => handleStatusClick(driver, e)}>
              {driver.status ? (
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium cursor-pointer hover:opacity-80 transition-opacity ${getStatusColor(driver.status)}`}>
                  {driver.status}
                </span>
              ) : (
                <span className="text-xs text-slate-400 cursor-pointer hover:text-slate-600">Click to set</span>
              )}
            </td>
            <td className="px-2 py-2">{driver.position ? <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getPositionColor(driver.position)}`}>{driver.position}</span> : '-'}</td>
            <td className="px-2 py-2 text-xs font-semibold text-slate-900">{driver.company || '-'}</td>
            <td className="px-2 py-2 text-xs text-slate-600">{driver.phone_number || '-'}</td>
            <td className="px-2 py-2 text-xs text-slate-600">{driver.email || '-'}</td>
            <td className="px-2 py-2 text-xs text-slate-600">{driver.commission_cpm || '-'}</td>
            <td className="px-2 py-2 text-xs text-slate-600">{driver.reference || '-'}</td>
            <td className="px-2 py-2 text-xs text-slate-600">{driver.application || '-'}</td>
            <td className="px-2 py-2 text-xs text-slate-600">{driver.mvr || '-'}</td>
            <td className="px-2 py-2 text-xs text-slate-600">{driver.psp || '-'}</td>
            <td className="px-2 py-2 text-xs text-slate-600">{driver.medical || '-'}</td>
            <td className="px-2 py-2 text-xs text-slate-600">{driver.cdl_mvr_match || '-'}</td>
            <td className="px-2 py-2 text-xs text-slate-600">{driver.medical_mvr_match || '-'}</td>
            <td className="px-2 py-2 text-xs text-slate-600">{driver.clh || '-'}</td>
            <td className="px-2 py-2 text-xs text-slate-600">{driver.two_year_proof || '-'}</td>
            <td className="px-2 py-2 text-xs text-slate-600">{driver.cdl_scan || '-'}</td>
            <td className="px-2 py-2 text-xs text-slate-600">{driver.drug_test || '-'}</td>
            <td className="px-2 py-2 text-xs text-slate-600">{driver.last_updated ? new Date(driver.last_updated).toLocaleDateString() : '-'}</td>
            <td className="px-2 py-2" onClick={(e) => e.stopPropagation()}>
              <select
                value={driver.category}
                onChange={(e) => handleCategoryChange(driver.id, e.target.value as DriverCategory)}
                className="px-2 py-1 border border-slate-300 rounded text-[10px] font-medium bg-white"
                title="Move to category"
              >
                {CATEGORY_ORDER.map(c => (
                  <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>
                ))}
              </select>
            </td>
          </>
        )}
      </tr>
    ));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-[1900px] mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-green-100 p-3 rounded-lg">
                <Users className="w-8 h-8 text-green-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">All Drivers - Recruiting</h1>
                <p className="text-slate-600">Track all driver recruitment progress</p>
              </div>
            </div>
            <button
              onClick={() => setShowAddForm(true)}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <Plus className="w-5 h-5" />
              Add Driver
            </button>
          </div>

          {/* Info Banner */}
          <div className="mt-6 bg-blue-50 border-l-4 border-blue-500 p-4 rounded-lg">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-blue-800">
                  <strong>Auto-sync rule:</strong> When a recruiter sets a driver's status to <strong>"Approved"</strong>, <strong>"Drug Test Scheduled"</strong>, or <strong>"Drug Test Pass"</strong> in the CRM page, that driver will automatically appear in the "ALL DRIVERS" category here.
                </p>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="mt-4 flex flex-wrap gap-4">
            <div className="flex-1 min-w-[300px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search by name, email, or phone..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>
            </div>
            <select
              value={filterPerson}
              onChange={(e) => setFilterPerson(e.target.value)}
              className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="">All Recruiters</option>
              {recruiters.map(recruiter => (
                <option key={recruiter} value={recruiter}>{recruiter}</option>
              ))}
            </select>
            <select
              value={filterCompany}
              onChange={(e) => setFilterCompany(e.target.value)}
              className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="">All Companies</option>
              {companyOptions.map(company => (
                <option key={company} value={company}>{company}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Status Change Modal */}
        {showStatusModal && selectedDriverForStatus && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
              <div className="p-6 border-b border-slate-200 flex items-center justify-between">
                <h2 className="text-xl font-bold text-slate-900">Change Status</h2>
                <button onClick={() => setShowStatusModal(false)} className="text-slate-400 hover:text-slate-600">
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="p-6">
                <div className="mb-4">
                  <p className="text-sm text-slate-600 mb-2">Driver: <strong>{selectedDriverForStatus.name}</strong></p>
                  <p className="text-sm text-slate-600">Current Status: <strong>{selectedDriverForStatus.status || 'None'}</strong></p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">New Status</label>
                  <select
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="">Select Status</option>
                    {statusOptions.map(status => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="p-6 border-t border-slate-200 flex justify-end gap-3">
                <button
                  onClick={() => setShowStatusModal(false)}
                  className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleStatusUpdate}
                  disabled={!newStatus}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Update Status
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Pipeline — Leads in Review */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 mb-6 overflow-hidden">
          <div
            onClick={() => setPipelineExpanded(!pipelineExpanded)}
            className="px-4 py-3 cursor-pointer flex items-center justify-between bg-gradient-to-r from-indigo-50 to-indigo-100 hover:from-indigo-100 hover:to-indigo-200 border-l-4 border-indigo-500"
          >
            <div>
              <h3 className="font-bold text-slate-900 text-base">Pipeline — Leads in Review</h3>
              <p className="text-xs text-slate-600">
                Leads with status Approved, Drug Test Scheduled or Drug Test Pass — mark them Hired when they pass
              </p>
              <p className="text-[11px] text-slate-500 mt-1">{pipelineLeads.length} lead{pipelineLeads.length === 1 ? '' : 's'}</p>
            </div>
            <div className={`transform transition-transform duration-200 ${pipelineExpanded ? 'rotate-180' : ''}`}>
              <ChevronDown className="w-5 h-5 text-slate-600" />
            </div>
          </div>
          {pipelineExpanded && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-3 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Name</th>
                    <th className="px-3 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Contact</th>
                    <th className="px-3 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Interested In</th>
                    <th className="px-3 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Status</th>
                    <th className="px-3 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Recruiter</th>
                    <th className="px-3 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Move to</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {pipelineLeads.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-xs text-slate-500">
                        No leads currently in pipeline-ready statuses.
                      </td>
                    </tr>
                  ) : pipelineLeads.map(lead => {
                    const displayName = lead.name || `${lead.first_name || ''} ${lead.last_name || ''}`.trim() || '—';
                    const statusLabel = lead.status
                      .replace(/_/g, ' ')
                      .replace(/\b\w/g, c => c.toUpperCase());
                    return (
                      <tr key={lead.id} className="hover:bg-slate-50">
                        <td className="px-3 py-2 align-top text-xs font-medium text-slate-900">{displayName}</td>
                        <td className="px-3 py-2 align-top">
                          {lead.email && <div className="text-xs text-indigo-600">{lead.email}</div>}
                          {lead.phone && <div className="text-xs text-slate-600 mt-0.5">{lead.phone}</div>}
                          {!lead.email && !lead.phone && <span className="text-xs text-slate-400">—</span>}
                        </td>
                        <td className="px-3 py-2 align-top text-xs text-slate-700">{lead.interested_in || '—'}</td>
                        <td className="px-3 py-2 align-top">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-indigo-100 text-indigo-800">
                            {statusLabel}
                          </span>
                        </td>
                        <td className="px-3 py-2 align-top text-xs text-slate-700">{lead.recruiter || '—'}</td>
                        <td className="px-3 py-2 align-top">
                          <select
                            value=""
                            disabled={promotingLeadId === lead.id}
                            onChange={(e) => {
                              const value = e.target.value as DriverCategory | '';
                              if (value) moveLeadToPipeline(lead, value);
                            }}
                            className="px-2 py-1 border border-slate-300 rounded text-[11px] font-medium bg-white disabled:opacity-50"
                          >
                            <option value="">{promotingLeadId === lead.id ? 'Moving…' : 'Move to…'}</option>
                            {CATEGORY_ORDER.map(c => (
                              <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Categories Table */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-x-auto">
          <table className="w-full min-w-max">
            <tbody>
              {/* ALL DRIVERS Section */}
              <tr>
                <td colSpan={23} className="p-0">
                  <div
                    onClick={() => setAllDriversExpanded(!allDriversExpanded)}
                    className="px-4 py-3 cursor-pointer flex items-center justify-between bg-gradient-to-r from-green-50 to-green-100 hover:from-green-100 hover:to-green-200 border-l-4 border-green-500"
                  >
                    <div>
                      <h3 className="font-bold text-slate-900 text-base">ALL DRIVERS</h3>
                      <p className="text-xs text-slate-600">{allDriversEntries.length} drivers</p>
                    </div>
                    <div className={`transform transition-transform duration-200 ${allDriversExpanded ? 'rotate-180' : ''}`}>
                      <ChevronDown className="w-5 h-5 text-slate-600" />
                    </div>
                  </div>
                </td>
              </tr>
              {allDriversExpanded && (
                <>
                  <tr>
                    <td colSpan={23} className="p-0">
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">✓</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Name</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Person</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Date of Arrival</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Status</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Position</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Company</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Phone</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Email</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Commission/CPM</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Reference</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Application</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">MVR</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">PSP</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Medical</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">CDL-MVR Match</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Medical-MVR Match</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">CLH</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">2 Year Proof</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">CDL Scan</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Drug Test</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Last Updated</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Move</th>
                  </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-200">
                            {renderTableRows(allDriversEntries)}
                          </tbody>
                        </table>
                      </div>
                    </td>
                  </tr>
                </>
              )}

              {/* READY - WAITING FOR FREE TRUCK Section */}
              <tr>
                <td colSpan={23} className="p-0">
                  <div
                    onClick={() => setReadyWaitingExpanded(!readyWaitingExpanded)}
                    className="px-4 py-3 cursor-pointer flex items-center justify-between bg-gradient-to-r from-purple-50 to-purple-100 hover:from-purple-100 hover:to-purple-200 border-l-4 border-purple-500"
                  >
                    <div>
                      <h3 className="font-bold text-slate-900 text-base">READY - WAITING FOR FREE TRUCK</h3>
                      <p className="text-xs text-slate-600">{readyWaitingEntries.length} drivers</p>
                    </div>
                    <div className={`transform transition-transform duration-200 ${readyWaitingExpanded ? 'rotate-180' : ''}`}>
                      <ChevronDown className="w-5 h-5 text-slate-600" />
                    </div>
                  </div>
                </td>
              </tr>
              {readyWaitingExpanded && (
                <>
                  <tr>
                    <td colSpan={23} className="p-0">
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">✓</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Name</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Person</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Date of Arrival</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Status</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Position</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Company</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Phone</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Email</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Commission/CPM</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Reference</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Application</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">MVR</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">PSP</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Medical</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">CDL-MVR Match</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Medical-MVR Match</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">CLH</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">2 Year Proof</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">CDL Scan</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Drug Test</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Last Updated</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Move</th>
                  </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-200">
                            {renderTableRows(readyWaitingEntries)}
                          </tbody>
                        </table>
                      </div>
                    </td>
                  </tr>
                </>
              )}

              {/* HIRED DRIVERS Section */}
              <tr>
                <td colSpan={23} className="p-0">
                  <div
                    onClick={() => setHiredExpanded(!hiredExpanded)}
                    className="px-4 py-3 cursor-pointer flex items-center justify-between bg-gradient-to-r from-blue-50 to-blue-100 hover:from-blue-100 hover:to-blue-200 border-l-4 border-blue-500"
                  >
                    <div>
                      <h3 className="font-bold text-slate-900 text-base">HIRED DRIVERS</h3>
                      <p className="text-xs text-slate-600">{hiredEntries.length} drivers</p>
                    </div>
                    <div className={`transform transition-transform duration-200 ${hiredExpanded ? 'rotate-180' : ''}`}>
                      <ChevronDown className="w-5 h-5 text-slate-600" />
                    </div>
                  </div>
                </td>
              </tr>
              {hiredExpanded && (
                <>
                  <tr>
                    <td colSpan={23} className="p-0">
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">✓</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Name</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Person</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Date of Arrival</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Status</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Position</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Company</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Phone</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Email</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Commission/CPM</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Reference</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Application</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">MVR</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">PSP</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Medical</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">CDL-MVR Match</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Medical-MVR Match</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">CLH</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">2 Year Proof</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">CDL Scan</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Drug Test</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Last Updated</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Move</th>
                  </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-200">
                            {renderTableRows(hiredEntries)}
                          </tbody>
                        </table>
                      </div>
                    </td>
                  </tr>
                </>
              )}

              {/* RE-POSSIBLE FOR HIRE Section */}
              <tr>
                <td colSpan={23} className="p-0">
                  <div
                    onClick={() => setRePossibleExpanded(!rePossibleExpanded)}
                    className="px-4 py-3 cursor-pointer flex items-center justify-between bg-gradient-to-r from-yellow-50 to-yellow-100 hover:from-yellow-100 hover:to-yellow-200 border-l-4 border-yellow-500"
                  >
                    <div>
                      <h3 className="font-bold text-slate-900 text-base">RE-POSSIBLE FOR HIRE</h3>
                      <p className="text-xs text-slate-600">{rePossibleEntries.length} drivers</p>
                    </div>
                    <div className={`transform transition-transform duration-200 ${rePossibleExpanded ? 'rotate-180' : ''}`}>
                      <ChevronDown className="w-5 h-5 text-slate-600" />
                    </div>
                  </div>
                </td>
              </tr>
              {rePossibleExpanded && (
                <>
                  <tr>
                    <td colSpan={23} className="p-0">
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">✓</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Name</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Person</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Date of Arrival</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Status</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Position</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Company</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Phone</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Email</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Commission/CPM</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Reference</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Application</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">MVR</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">PSP</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Medical</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">CDL-MVR Match</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Medical-MVR Match</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">CLH</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">2 Year Proof</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">CDL Scan</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Drug Test</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Last Updated</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Move</th>
                  </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-200">
                            {renderTableRows(rePossibleEntries)}
                          </tbody>
                        </table>
                      </div>
                    </td>
                  </tr>
                </>
              )}

              {/* NOT HIRED - NOT RE-POSSIBLE FOR HIRE Section */}
              <tr>
                <td colSpan={23} className="p-0">
                  <div
                    onClick={() => setNotHiredExpanded(!notHiredExpanded)}
                    className="px-4 py-3 cursor-pointer flex items-center justify-between bg-gradient-to-r from-red-50 to-red-100 hover:from-red-100 hover:to-red-200 border-l-4 border-red-500"
                  >
                    <div>
                      <h3 className="font-bold text-slate-900 text-base">NOT HIRED - NOT RE-POSSIBLE FOR HIRE</h3>
                      <p className="text-xs text-slate-600">{notHiredEntries.length} drivers</p>
                    </div>
                    <div className={`transform transition-transform duration-200 ${notHiredExpanded ? 'rotate-180' : ''}`}>
                      <ChevronDown className="w-5 h-5 text-slate-600" />
                    </div>
                  </div>
                </td>
              </tr>
              {notHiredExpanded && (
                <>
                  <tr>
                    <td colSpan={23} className="p-0">
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">✓</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Name</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Person</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Date of Arrival</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Status</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Position</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Company</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Phone</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Email</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Commission/CPM</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Reference</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Application</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">MVR</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">PSP</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Medical</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">CDL-MVR Match</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Medical-MVR Match</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">CLH</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">2 Year Proof</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">CDL Scan</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Drug Test</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Last Updated</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase">Move</th>
                  </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-200">
                            {renderTableRows(notHiredEntries)}
                          </tbody>
                        </table>
                      </div>
                    </td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>

        {/* Stats Footer */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
          <div className="flex items-center justify-between text-sm text-slate-600">
            <div className="flex flex-wrap gap-6">
              <span>All Drivers: <strong className="text-green-600">{allDriversEntries.length}</strong></span>
              <span>Ready/Waiting: <strong className="text-purple-600">{readyWaitingEntries.length}</strong></span>
              <span>Hired: <strong className="text-blue-600">{hiredEntries.length}</strong></span>
              <span>Re-Possible: <strong className="text-yellow-600">{rePossibleEntries.length}</strong></span>
              <span>Not Hired: <strong className="text-red-600">{notHiredEntries.length}</strong></span>
              <span>Total: <strong className="text-slate-900">{drivers.length}</strong></span>
            </div>
          </div>
        </div>

        {/* Action Bar */}
        {selectedIds.length > 0 && (
          <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 z-50">
            <div className="bg-slate-900 text-white px-6 py-3 rounded-full shadow-lg flex items-center gap-4">
              <span className="font-medium">{selectedIds.length} selected</span>
              <button
                onClick={() => {
                  if (selectedIds.length === 1) {
                    const driver = drivers.find(d => d.id === selectedIds[0]);
                    if (driver) {
                      handleEdit(driver);
                      setSelectedIds([]);
                    }
                  }
                }}
                disabled={selectedIds.length !== 1}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Edit2 className="w-4 h-4" />
                Edit
              </button>
              <button
                onClick={handleSendToSafety}
                disabled={selectedIds.length !== 1 || sendingToSafety || (selectedIds.length === 1 && drivers.find(d => d.id === selectedIds[0])?.category !== 'hired')}
                title={selectedIds.length === 1 && drivers.find(d => d.id === selectedIds[0])?.category !== 'hired' ? 'Driver must be in HIRED' : 'Promote to drivers table'}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Shield className="w-4 h-4" />
                {sendingToSafety ? 'Sending…' : 'Send to Safety'}
              </button>
              <button
                onClick={async () => {
                  if (window.confirm(`Delete ${selectedIds.length} driver(s)?`)) {
                    for (const id of selectedIds) {
                      await handleDelete(id);
                    }
                    setSelectedIds([]);
                  }
                }}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
              <button
                onClick={() => setSelectedIds([])}
                className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
